import { TabletButton } from '@campfire/tablet-button';
import { Box, Checkbox, DialogActions, DialogContent, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

interface Session {
  sessionId: string;
  name: string;
  startTime: string | null;
  endTime: string | null;
}

interface EventDate {
  date: string;
  sessions: Session[];
}

interface EventSessionPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selections: Array<{ date: string; sessionIds: string[] }>) => void;
  eventDates: EventDate[];
  activityName: string;
  hasOpenRoster: boolean;
  isLoading?: boolean;
}

const formatTime = (time: string | null): string => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'pm' : 'am';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch (_unusedErr) {
    return time;
  }
};

const formatDate = (dateStr: string): { day: string; monthYear: string; weekday: string } => {
  const dt = DateTime.fromISO(dateStr);
  return {
    day: dt.toFormat('d'),
    monthYear: dt.toFormat('MMM yyyy'),
    weekday: dt.toFormat('cccc'),
  };
};

export const EventSessionPickerDialog = ({
  open,
  onClose,
  onConfirm,
  eventDates,
  activityName,
  hasOpenRoster,
  isLoading,
}: EventSessionPickerDialogProps) => {
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const { isMobile, theme } = useCampfireTheme();

  const toggleSession = (date: string, sessionId: string) => {
    setSelections((prev) => {
      const current = prev[date] ?? [];
      const updated = current.includes(sessionId) ? current.filter((id) => id !== sessionId) : [...current, sessionId];
      return { ...prev, [date]: updated };
    });
  };

  const toggleDate = (date: string, sessionIds: string[]) => {
    setSelections((prev) => {
      const current = prev[date] ?? [];
      const allSelected = sessionIds.every((id) => current.includes(id));
      return { ...prev, [date]: allSelected ? [] : sessionIds };
    });
  };

  const totalSelected = Object.values(selections).reduce((sum, ids) => sum + ids.length, 0);
  const datesSelected = Object.entries(selections).filter(([_unusedKey, ids]) => ids.length > 0).length;

  const handleConfirm = () => {
    const result = Object.entries(selections)
      .filter(([_unusedKey, sessionIds]) => sessionIds.length > 0)
      .map(([date, sessionIds]) => ({ date, sessionIds }));
    onConfirm(result);
  };

  return (
    <FullscreenDialog
      open={open}
      close={onClose}
      fullScreen={isMobile}
      title='Select Your Dates & Sessions'
      scroll='body'
      maxWidth='md'
    >
      <DialogContent style={{ minWidth: isMobile ? 290 : 760, paddingTop: 24 }}>
        <Typography style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{activityName}</Typography>
        <Typography variant='body2' color='textSecondary' style={{ marginBottom: 20 }}>
          {hasOpenRoster
            ? 'Select the dates and sessions you want to attend. You will be rostered immediately.'
            : 'Select the dates and sessions you are available for. The manager will finalise the roster.'}
        </Typography>

        <Box display='flex' overflowX='auto' overflowY='hidden' pb={2} style={{ gap: 16 }}>
          {eventDates.map((eventDate) => {
            const { day, monthYear, weekday } = formatDate(eventDate.date);
            const dateSessionIds = eventDate.sessions.map((s) => s.sessionId);
            const selectedForDate = selections[eventDate.date] ?? [];

            return (
              <Box
                key={eventDate.date}
                minWidth={isMobile ? 200 : 260}
                borderRadius={12}
                border={`1px solid ${
                  selectedForDate.length > 0 ? theme.palette.primary.main : theme.color.grey.border
                }`}
                bgcolor={selectedForDate.length > 0 ? 'rgba(121, 111, 255, 0.03)' : '#fff'}
                overflow='hidden'
                flexShrink={0}
                style={{ transition: 'border-color 0.15s' }}
              >
                <Box
                  px={2}
                  py={1.5}
                  bgcolor={selectedForDate.length > 0 ? theme.palette.primary.main : theme.color.grey.neutralBrand100}
                  style={{ cursor: 'pointer', transition: 'background-color 0.15s' }}
                  onClick={() => toggleDate(eventDate.date, dateSessionIds)}
                >
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box>
                      <Typography
                        style={{
                          fontWeight: 800,
                          fontSize: 22,
                          color: selectedForDate.length > 0 ? '#fff' : theme.color.grey.neutral400,
                          lineHeight: 1.1,
                        }}
                      >
                        {day}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: selectedForDate.length > 0 ? 'rgba(255,255,255,0.8)' : theme.color.grey.neutral200,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {monthYear}
                      </Typography>
                    </Box>
                    <Typography
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: selectedForDate.length > 0 ? 'rgba(255,255,255,0.7)' : theme.color.grey.neutral200,
                      }}
                    >
                      {weekday}
                    </Typography>
                  </Box>
                </Box>

                <Box px={1} py={1}>
                  {eventDate.sessions.map((session) => {
                    const isSelected = selectedForDate.includes(session.sessionId);
                    const timeLabel =
                      session.startTime && session.endTime
                        ? `${formatTime(session.startTime)} – ${formatTime(session.endTime)}`
                        : null;

                    return (
                      <Box
                        key={session.sessionId}
                        display='flex'
                        alignItems='center'
                        px={1}
                        py={0.5}
                        borderRadius={6}
                        style={{ cursor: 'pointer' }}
                        bgcolor={isSelected ? 'rgba(121, 111, 255, 0.06)' : 'transparent'}
                        onClick={() => toggleSession(eventDate.date, session.sessionId)}
                      >
                        <Checkbox
                          checked={isSelected}
                          color='primary'
                          size='small'
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSession(eventDate.date, session.sessionId)}
                          style={{ padding: 4 }}
                        />
                        <Box ml={0.5}>
                          <Typography style={{ fontSize: 13, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>
                            {session.name}
                          </Typography>
                          {timeLabel && (
                            <Typography style={{ fontSize: 11, color: theme.color.grey.neutral200 }}>
                              {timeLabel}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', borderTop: `1px solid ${theme.color.grey.border}` }}>
        <Box display='flex' alignItems='center' style={{ marginRight: 'auto' }}>
          <Typography variant='body2' style={{ color: theme.color.grey.neutral200, fontWeight: 600 }}>
            {datesSelected} date{datesSelected !== 1 ? 's' : ''}, {totalSelected} session
            {totalSelected !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <TabletButton size='medium' onClick={onClose} style={{ textTransform: 'none', marginRight: 8 }}>
          Skip for now
        </TabletButton>
        <TabletButton
          size='medium'
          variant='contained'
          color='primary'
          disabled={totalSelected === 0 || isLoading}
          onClick={handleConfirm}
          style={{ textTransform: 'none' }}
        >
          {hasOpenRoster ? 'Confirm & Roster' : 'Confirm Availability'}
        </TabletButton>
      </DialogActions>
    </FullscreenDialog>
  );
};
